import { MktButton, MktFeature, MktHero, MktSection, MktTrustItem } from "../components/marketing";
import { Seo } from "../lib/seo";

/**
 * Public /features page — a deeper, truthful tour of what ODIN does.
 *
 * Every block describes how a capability is designed to work. Nothing here claims
 * an unfinished feature is live, and there are no fabricated metrics, testimonials
 * or guarantees.
 */
export function Features(): JSX.Element {
  return (
    <>
      <Seo
        title="Features — design, review and safely deploy your Discord community"
        description="Blueprints, safe dry-run and approval, deployment locks, BALDER AI assistance, moderation, integrations and more — a calm end-to-end workflow for one Discord server."
        path="/features"
      />
      <MktHero
        kicker="Features"
        title="Design, review, and safely deploy your community"
        subhead="ODIN gives one Discord community a calm, end-to-end workflow — with safety rails at every step and BALDER on hand to explain what is happening."
      >
        <MktButton to="/login" variant="primary">
          Get started
        </MktButton>
        <MktButton to="/pricing" variant="secondary">
          View pricing
        </MktButton>
      </MktHero>

      <MktSection kicker="Capabilities" title="What ODIN helps you do">
        <div className="mkt-grid">
          <MktFeature
            title="Design & blueprints"
            desc="Shape roles, channels, categories and onboarding as a versioned blueprint. Start from a template and adapt it, keeping a clear record of the intended structure."
          />
          <MktFeature
            title="Safe dry run & approval"
            desc="Every change is previewed as a dry run showing exactly what would be created, changed or removed. Nothing is applied until you explicitly approve it."
          />
          <MktFeature
            title="Deployment locks"
            desc="Live deployment to a real Discord server is locked by default. Enabling it is a deliberate, opt-in step, so a draft can never reach your community by accident."
          />
          <MktFeature
            title="BALDER, the AI companion"
            desc="BALDER explains steps in plain language, prepares blueprint drafts and produces dry-run previews. By design it never deploys and never approves changes on its own."
          />
          <MktFeature
            title="Moderation & automod"
            desc="Configure moderation rules and automated moderation for your community, reviewed the same careful way as any other change before it takes effect."
          />
          <MktFeature
            title="Community tools"
            desc="Run tickets, polls and events to keep your members engaged, managed from the same place as the rest of your server."
          />
          <MktFeature
            title="Integrations"
            desc="Connect n8n automations, RSS feeds, streaming (Twitch/YouTube) and game-server notifications. Credentials are stored server-side and never shown back to the browser."
          />
          <MktFeature
            title="Storage & exports"
            desc="Keep artifacts and exports of your configuration so you have a record of what your server looked like at each step."
          />
          <MktFeature
            title="Billing & entitlements"
            desc="Free, Core, Pro and Dedicated plans gate features through clear entitlements. All prices include VAT; Dedicated is quoted individually and shown as a starting price."
          />
        </div>
      </MktSection>

      <MktSection
        kicker="Safety rails"
        title="Every change goes through review-and-apply"
        lead="The workflow is built so surprises can't reach your community. You stay in control at each step."
      >
        <div className="mkt-trust">
          <MktTrustItem
            title="See it before it happens"
            desc="A dry run lays out the full plan — additions, changes and removals — so you review a change before it is ever applied."
          />
          <MktTrustItem
            title="Approval is yours alone"
            desc="Nothing structural is applied without your explicit approval. BALDER can prepare and explain, but it never approves."
          />
          <MktTrustItem
            title="Reversible releases"
            desc="Snapshots and release reports let you review each release and roll it back if you need to."
          />
          <MktTrustItem
            title="A clear record"
            desc="Actions are recorded so you can see what changed, when, and who approved it."
          />
        </div>
      </MktSection>

      <MktSection kicker="Honesty" title="What we don't claim">
        <p className="mkt-section__lead">
          ODIN is rolling out carefully. We make no uptime promises and publish the platform's current
          state openly. Where a capability is still being prepared, we say so instead of implying it is
          already live.
        </p>
      </MktSection>

      <div className="mkt-container">
        <div className="mkt-cta">
          <h2>Ready to shape your server?</h2>
          <p>Connect your server, preview every change, and approve what goes live.</p>
          <div className="mkt-hero__actions">
            <MktButton to="/login" variant="primary">
              Get started
            </MktButton>
            <MktButton to="/pricing" variant="secondary">
              Compare plans
            </MktButton>
          </div>
        </div>
      </div>
    </>
  );
}
